/*
 * Layered entity-structure / step-plan chart layout (pure — no Office.js
 * imports, unit-tested).
 *
 * Emits primitives (see primitives.ts) for an M&A / legal entity structure
 * chart. Unlike orgChart this is a layered DAG, not a tree: an entity may have
 * MULTIPLE ownership parents (e.g. a joint venture). Tiers come from a
 * longest-path assignment (ultimate owners at the top), within-tier order is
 * settled with a barycenter heuristic to reduce edge crossings, node geometry
 * is scaled to stay inside the region, entities render as shape-by-type boxes,
 * ownership renders as elbow connectors with percentage labels, and asset/cash
 * flows render as a separate dashed, colored, arrow-headed layer on top.
 * Deterministic for a given input (stable sorts, no Date/Math.random).
 */

import {
  BoxPrim,
  DEFAULT_PALETTE,
  DEFAULT_REGION,
  LinePrim,
  Palette,
  Primitive,
  Region,
  TextPrim,
} from "./primitives";

export type EntityType =
  | "corp"
  | "llc"
  | "lp"
  | "llp"
  | "partnership"
  | "individual"
  | "trust"
  | "spv"
  | "foreign"
  | "disregarded"
  | "branch"
  | "other";

export interface Entity {
  id: string;
  name: string;
  type?: EntityType;
  jurisdiction?: string;
  note?: string;
  /** Box fill, hex without '#'. Overrides the per-type default tint. */
  fill?: string;
}

export interface Ownership {
  parent: string;
  child: string;
  pct?: number;
  dashed?: boolean;
}

export interface Flow {
  from: string;
  to: string;
  label?: string;
  kind?: "cash" | "equity" | "asset" | "debt" | "other";
  dashed?: boolean;
  color?: string;
}

export interface StructureModel {
  entities: Entity[];
  ownership: Ownership[];
  flows?: Flow[];
}

/* Layout constants (points). */
const GAP = 24; // horizontal gap between sibling nodes within a tier
const NODE_W_MIN = 96;
const NODE_W_MAX = 190;
const NODE_W_FLOOR = 72; // soft floor; shrink below only to prevent overlap
const NODE_H = 46;
const NODE_H_SUB = 54; // row height when any node in the tier has a sub-line
const NODE_H_FLOOR = 34;
const ROW_GAP_MIN = 46;
const ROW_GAP_FLOOR = 16;
const OWN_COLOR = "94A3B8"; // neutral gray ownership edges
const OWN_WEIGHT = 1.25;
const BOX_FILL = "F8FAFC"; // near-white; legal charts are black-on-white

/* Default tint per entity class — restrained, print-safe, and enough contrast to
 * tell operating companies from holdcos, people, and outside parties at a glance.
 * An entity's own `fill` overrides this. */
const FILL_BY_TYPE: { [k: string]: string } = {
  corp: "EEF2FF", // indigo tint — corporations
  llc: "F0FDF4", // green tint — operating LLCs
  lp: "FEFCE8", // amber tint — partnerships / JV vehicles
  llp: "FEFCE8",
  partnership: "FEFCE8",
  individual: "FFFFFF", // people stay white
  trust: "FDF4FF", // violet tint
  spv: "F0F9FF",
  foreign: "FEF2F2", // red tint — outside the jurisdiction
  disregarded: "F8FAFC",
  branch: "F8FAFC",
  other: "F8FAFC",
};

function fillFor(e: Entity): string {
  if (e.fill) return e.fill.charAt(0) === "#" ? e.fill.substring(1) : e.fill;
  return (e.type && FILL_BY_TYPE[e.type]) || BOX_FILL;
}
const BOX_BORDER_WEIGHT = 1.25;
const ARROW = 10; // flow arrowhead triangle size
const FLOW_WEIGHT = 1.5;
const FLOW_OFFSET = 8; // lateral nudge so flows read separately from ownership
const FLOW_LANE_GAP = 13; // lateral spacing between parallel flows on the same pair

/* Default flow colors by kind (hex without '#'; the pane adds it). */
const FLOW_COLORS: { [k: string]: string } = {
  cash: "059669",
  equity: "2563EB",
  asset: "D97706",
  debt: "DC2626",
  other: "6B7280",
};

/* Entity type -> box shape (legal/accounting convention). */
function shapeFor(type?: EntityType): BoxPrim["kind"] {
  switch (type) {
    case "llc":
      return "roundRect";
    case "lp":
    case "llp":
    case "partnership":
      return "parallelogram";
    case "individual":
      return "ellipse";
    case "trust":
      return "hexagon";
    default:
      // corp, spv, foreign, disregarded, branch, other, undefined
      return "rect";
  }
}

/* Dashed border = disregarded / foreign / branch entity. */
function dashedFor(type?: EntityType): boolean {
  return type === "foreign" || type === "disregarded" || type === "branch";
}

const TYPE_LABEL: { [k: string]: string } = {
  corp: "Corp",
  llc: "LLC",
  lp: "LP",
  llp: "LLP",
  partnership: "Partnership",
  individual: "Individual",
  trust: "Trust",
  spv: "SPV",
  foreign: "Foreign",
  disregarded: "Disregarded",
  branch: "Branch",
  other: "",
};

/** "Sabre Corp" + type Corp rendered as "Sabre Corp / Corp"; every "DM Scranton,
 * LLC" carried a redundant "LLC ·" prefix. Drop the type label when the name
 * already ends with it. */
function nameCarriesType(name: string, typeLabel: string): boolean {
  if (!typeLabel) return false;
  const clean = name.replace(/[\s.,;:]+$/, "").toLowerCase();
  const t = typeLabel.toLowerCase();
  if (!clean.endsWith(t)) return false;
  const before = clean.charAt(clean.length - t.length - 1);
  return before === "" || before === " " || before === "," || before === ".";
}

function subLine(e: Entity): string | undefined {
  const parts: string[] = [];
  const t = e.type ? TYPE_LABEL[e.type] : "";
  if (t && !nameCarriesType(e.name, t)) parts.push(t);
  if (e.jurisdiction) parts.push(e.jurisdiction);
  if (e.note) parts.push(e.note);
  const s = parts.join(" · ");
  return s || undefined;
}

function stripHash(c?: string): string | undefined {
  if (!c) return undefined;
  return c.charAt(0) === "#" ? c.substring(1) : c;
}

interface Node {
  entity: Entity;
  index: number; // input order (stable tie-break)
  tier: number;
  posInTier: number;
  parents: number[]; // node indices
  children: number[]; // node indices
  cx: number;
  fontPt: number; // resolved by the text fitter; the box renders at this size
  box: { x: number; y: number; w: number; h: number };
}

/** How well the diagram fit the region it was given. The op handler surfaces this
 * so the agent can decide, on evidence, whether to give the diagram its own slide
 * or split it — instead of guessing at density. */
export interface FitReport {
  entities: number;
  tiers: number;
  widest_tier: number; // most entities in any one row
  node_w: number; // resolved box width (points)
  min_font_pt: number; // smallest entity font after step-down
  cramped: boolean; // hit a width/height/font floor — legibility is at risk
  recommend_min_w: number; // region width (points) at which it would NOT be cramped
  advice?: string; // present only when cramped
}

export function structureChartLayout(
  model: StructureModel,
  region?: Region,
  palette?: Palette,
  report?: FitReport
): Primitive[] {
  const reg = region || DEFAULT_REGION;
  const pal = palette || DEFAULT_PALETTE;
  if (!model || !model.entities || model.entities.length === 0) return [];

  const entities = model.entities;
  const nodes: Node[] = [];
  const byId: { [id: string]: number } = {};
  for (let i = 0; i < entities.length; i++) {
    byId[entities[i].id] = i;
    nodes.push({
      entity: entities[i],
      index: i,
      tier: 0,
      posInTier: 0,
      parents: [],
      children: [],
      cx: 0,
      fontPt: 11,
      box: { x: 0, y: 0, w: 0, h: 0 },
    });
  }

  // Ownership edges (drop edges with missing endpoints; keep input order).
  const ownership = model.ownership || [];
  const edges: { p: number; c: number; own: Ownership }[] = [];
  for (let i = 0; i < ownership.length; i++) {
    const o = ownership[i];
    const p = byId[o.parent];
    const c = byId[o.child];
    if (p == null || c == null || p === c) continue;
    edges.push({ p: p, c: c, own: o });
    nodes[p].children.push(c);
    nodes[c].parents.push(p);
  }

  // 1. Tier assignment: longest path from roots, relaxed to a fixed point.
  // Roots (no incoming edge) stay at tier 0. Cap iterations at n to defend
  // against cycles — cyclic stragglers keep their last tier.
  const n = nodes.length;
  for (let iter = 0; iter < n; iter++) {
    let changed = false;
    for (let e = 0; e < edges.length; e++) {
      const pt = nodes[edges[e].p].tier;
      if (nodes[edges[e].c].tier < pt + 1) {
        nodes[edges[e].c].tier = pt + 1;
        changed = true;
      }
    }
    if (!changed) break;
  }

  // 1b. Pull sources down to just above their earliest child. Longest-path tiering
  // strands a root whose only child is deep — an external JV partner sat at tier 0
  // while the JV sat at tier 4, so its connector plunged down the whole right
  // margin through empty space and read as a stray wire. A source has no incoming
  // edge, so lowering it can never invert an edge: it still ends one tier above
  // its earliest child. Repeat to a fixed point (a source may feed another source).
  for (let iter = 0; iter < n; iter++) {
    let changed = false;
    for (let i = 0; i < n; i++) {
      if (nodes[i].parents.length > 0) continue; // not a source
      let minChild = Infinity;
      for (let e = 0; e < edges.length; e++) {
        if (edges[e].p === i) {
          const ct = nodes[edges[e].c].tier;
          if (ct < minChild) minChild = ct;
        }
      }
      if (minChild === Infinity) continue; // isolated node — leave at tier 0
      const target = minChild - 1;
      if (target > nodes[i].tier) {
        nodes[i].tier = target;
        changed = true;
      }
    }
    if (!changed) break;
  }

  // Pulling sources down can leave tier 0 (or an intermediate tier) empty, which
  // would divide by zero in the barycenter sweep. Compact to contiguous tiers.
  {
    const used: number[] = [];
    const seen: { [t: number]: boolean } = {};
    for (let i = 0; i < n; i++) {
      if (!seen[nodes[i].tier]) {
        seen[nodes[i].tier] = true;
        used.push(nodes[i].tier);
      }
    }
    used.sort((a, b) => a - b);
    const remap: { [t: number]: number } = {};
    for (let k = 0; k < used.length; k++) remap[used[k]] = k;
    for (let i = 0; i < n; i++) nodes[i].tier = remap[nodes[i].tier];
  }

  // Group into tiers, preserving input order as the initial within-tier order.
  let maxTier = 0;
  for (let i = 0; i < n; i++) if (nodes[i].tier > maxTier) maxTier = nodes[i].tier;
  const T = maxTier + 1;
  const tiers: number[][] = [];
  for (let t = 0; t < T; t++) tiers[t] = [];
  for (let i = 0; i < n; i++) tiers[nodes[i].tier].push(i);
  for (let t = 0; t < T; t++) syncPos(tiers[t], nodes);

  // 2. Within-tier ordering: barycenter sweeps (down by parents, up by
  // children). Tier 0 (roots) keeps input order. ~4 sweeps.
  const norm = (ni: number) => (nodes[ni].posInTier + 0.5) / tiers[nodes[ni].tier].length;
  const settleTier = (t: number, neighborsOf: (ni: number) => number[]) => {
    const arr = tiers[t];
    const keys: number[] = [];
    for (let k = 0; k < arr.length; k++) {
      const nb = neighborsOf(arr[k]);
      if (nb.length === 0) {
        keys[k] = norm(arr[k]); // no neighbors: hold current position
      } else {
        let sum = 0;
        for (let j = 0; j < nb.length; j++) sum += norm(nb[j]);
        keys[k] = sum / nb.length;
      }
    }
    stableSortByKey(arr, keys);
    syncPos(arr, nodes);
  };
  for (let sweep = 0; sweep < 4; sweep++) {
    if (sweep % 2 === 0) {
      for (let t = 1; t < T; t++) settleTier(t, (ni) => nodes[ni].parents);
    } else {
      for (let t = T - 2; t >= 1; t--) settleTier(t, (ni) => nodes[ni].children);
    }
  }

  // 3./4. Geometry: node width from the widest tier, scaled to fit region.w.
  let maxCount = 1;
  for (let t = 0; t < T; t++) if (tiers[t].length > maxCount) maxCount = tiers[t].length;

  let gap = GAP;
  let nodeW = clamp(Math.floor((reg.w - gap * (maxCount + 1)) / maxCount), NODE_W_MIN, NODE_W_MAX);
  let contentW = maxCount * nodeW + (maxCount - 1) * gap;
  if (contentW > reg.w) {
    const f = reg.w / contentW;
    nodeW = nodeW * f;
    gap = gap * f;
    if (nodeW < NODE_W_FLOOR) {
      // Prefer the 72 floor if gaps can absorb the overflow; otherwise shrink
      // width below the floor so nothing overlaps and everything stays inside.
      const gapForFloor = maxCount > 1 ? (reg.w - maxCount * NODE_W_FLOOR) / (maxCount - 1) : reg.w - NODE_W_FLOOR;
      if (gapForFloor >= 0) {
        nodeW = NODE_W_FLOOR;
        gap = gapForFloor;
      }
    }
  }

  // Per-tier row heights, sized to the text the row ACTUALLY carries. A fixed
  // 54pt clipped long sub-lines mid-word: "Paper Mills JV, LLC / LP · Delaware ·
  // Joint" — an entity type truncated on a legal structure chart. The box renders
  // `name\nsub` at FONT_PT with no autofit, so measure the wrapped line count.
  const PAD_V = 12; // top+bottom inset PowerPoint leaves inside a shape
  const MAX_LINES = 3; // beyond this, step the font down rather than clip

  // A slanted or curved outline eats horizontal room, so its text wraps sooner.
  // Ignoring this is what clipped "…· Joint" inside the JV parallelogram: the
  // fitter measured against the full 147pt box width the text never had.
  const insetFor = (k: BoxPrim["kind"]): number => {
    switch (k) {
      case "parallelogram":
      case "hexagon":
      case "diamond":
      case "triangle":
      case "chevron":
        return 34;
      case "ellipse":
        return 22;
      default:
        return 12;
    }
  };

  const linesAt = (s: string | undefined, w: number, pt: number): number => {
    if (!s) return 0;
    const perLine = Math.max(1, Math.floor(w / (0.58 * pt)));
    return Math.max(1, Math.ceil(s.length / perLine));
  };

  // Largest font (11 → 10 → 9) whose wrapped text stays within MAX_LINES.
  const fitFont = (name: string, sub: string | undefined, w: number): { pt: number; lines: number } => {
    let last = { pt: 9, lines: MAX_LINES };
    for (const pt of [11, 10, 9]) {
      const lines = linesAt(name, w, pt) + linesAt(sub, w, pt);
      last = { pt: pt, lines: lines };
      if (lines <= MAX_LINES) return last;
    }
    return last; // 9pt and still long — the taller row below absorbs it
  };

  const rowH: number[] = [];
  let sumH = 0;
  for (let t = 0; t < T; t++) {
    let hasSub = false;
    let needed = NODE_H;
    for (let k = 0; k < tiers[t].length; k++) {
      const nd = nodes[tiers[t][k]];
      const e = nd.entity;
      const sub = subLine(e);
      if (sub) hasSub = true;
      const textW = Math.max(24, nodeW - insetFor(shapeFor(e.type)));
      const fit = fitFont(e.name, sub, textW);
      nd.fontPt = fit.pt; // the box prim renders at this size
      const h = Math.ceil(fit.lines * 1.35 * fit.pt + PAD_V);
      if (h > needed) needed = h;
    }
    rowH[t] = Math.max(hasSub ? NODE_H_SUB : NODE_H, needed);
    sumH += rowH[t];
  }

  // Vertical fit: spread rows evenly; if they overflow, compress the row gap
  // first (to a floor), then the node heights (to a floor).
  const rowY: number[] = [];
  if (T === 1) {
    rowY[0] = reg.y + (reg.h - rowH[0]) / 2;
  } else {
    let gapV = Math.max(ROW_GAP_MIN, (reg.h - sumH) / (T - 1));
    if (sumH + gapV * (T - 1) > reg.h) {
      gapV = Math.max(ROW_GAP_FLOOR, (reg.h - sumH) / (T - 1));
      if (sumH + gapV * (T - 1) > reg.h) {
        const budget = reg.h - gapV * (T - 1);
        const scaleH = budget / sumH;
        sumH = 0;
        for (let t = 0; t < T; t++) {
          rowH[t] = Math.max(NODE_H_FLOOR, rowH[t] * scaleH);
          sumH += rowH[t];
        }
        gapV = Math.max(0, (reg.h - sumH) / (T - 1));
      }
    }
    let y = reg.y;
    for (let t = 0; t < T; t++) {
      rowY[t] = y;
      y += rowH[t] + gapV;
    }
  }

  // Assign each node a box: tiers centered horizontally in the region.
  for (let t = 0; t < T; t++) {
    const arr = tiers[t];
    const count = arr.length;
    const tierContentW = count * nodeW + (count - 1) * gap;
    const startX = reg.x + (reg.w - tierContentW) / 2;
    for (let k = 0; k < count; k++) {
      const nd = nodes[arr[k]];
      let x = startX + k * (nodeW + gap);
      x = clamp(x, reg.x, reg.x + reg.w - nodeW); // safety: never exceed region
      nd.box = { x: x, y: rowY[t], w: nodeW, h: rowH[t] };
      nd.cx = x + nodeW / 2;
    }
  }

  // Horizontal refinement. Uniform, centred tiers leave a parent far from its only
  // child: an external JV partner sat at x492 while its JV sat at x749, so the
  // ownership line ran a 281pt horizontal leg through empty space. Pull each node
  // toward the median centre of its neighbours, then re-separate the row so the
  // pull can never overlap boxes or escape the region. Order within a tier is
  // preserved (the barycenter sweep already chose it), so no edge crossings appear.
  const median = (xs: number[]): number => {
    if (xs.length === 0) return NaN;
    const s = xs.slice().sort((a, b) => a - b);
    const m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  };

  const separate = (t: number) => {
    const arr = tiers[t];
    const minX = reg.x;
    const maxX = reg.x + reg.w - nodeW;
    const desired: number[] = [];
    for (let k = 0; k < arr.length; k++) desired[k] = nodes[arr[k]].box.x;

    // Left-to-right: never closer than `gap`.
    for (let k = 0; k < arr.length; k++) {
      const nd = nodes[arr[k]];
      let x = clamp(desired[k], minX, maxX);
      if (k > 0) {
        const floorX = nodes[arr[k - 1]].box.x + nodeW + gap;
        if (x < floorX) x = floorX;
      }
      nd.box.x = x;
    }
    // Right-to-left: undo any overshoot past the region's right edge.
    for (let k = arr.length - 1; k >= 0; k--) {
      const nd = nodes[arr[k]];
      let x = Math.min(nd.box.x, maxX);
      if (k < arr.length - 1) {
        const ceilX = nodes[arr[k + 1]].box.x - nodeW - gap;
        if (x > ceilX) x = ceilX;
      }
      nd.box.x = Math.max(minX, x);
    }

    // CENTRE the packed block on the mean desired position. Without this, a tier
    // whose nodes all want the same x (three shareholders over one company) is
    // anchored at that x and then pushed right by the spacing pass — which is what
    // skewed the whole chart to the right: every upper tier ended up centred on its
    // leftmost member's target instead of on its children.
    let sum = 0;
    for (let k = 0; k < arr.length; k++) sum += desired[k] - nodes[arr[k]].box.x;
    let shift = sum / arr.length;
    // Clamp the shift so the block stays inside the region.
    const first = nodes[arr[0]].box.x;
    const last = nodes[arr[arr.length - 1]].box.x;
    shift = clamp(shift, minX - first, maxX - last);
    for (let k = 0; k < arr.length; k++) {
      const nd = nodes[arr[k]];
      nd.box.x += shift;
      nd.cx = nd.box.x + nodeW / 2;
    }
  };

  const alignTo = (t: number, neighborsOf: (ni: number) => number[]) => {
    const arr = tiers[t];
    for (let k = 0; k < arr.length; k++) {
      const nd = nodes[arr[k]];
      const nb = neighborsOf(arr[k]);
      if (nb.length === 0) continue;
      const target = median(nb.map((ni) => nodes[ni].cx));
      if (!isFinite(target)) continue;
      nd.box.x = target - nodeW / 2;
    }
    separate(t);
  };

  // Finish with a DOWN pass so each parent ends up centred over its children —
  // that is the shape a reader expects. Ending on the up pass instead left the
  // holding company sitting over its own parent rather than over the five
  // subsidiaries it owns, dragging the whole spine off-centre.
  for (let pass = 0; pass < 2; pass++) {
    for (let t = 1; t < T; t++) alignTo(t, (ni) => nodes[ni].parents); // children under parents
    for (let t = T - 2; t >= 0; t--) alignTo(t, (ni) => nodes[ni].children); // parents over children
  }

  const prims: Primitive[] = [];

  // 6. Ownership connectors (drawn first; boxes render on top), grouped by child
  // so ownership lines NEVER cross.
  //  - FAN-IN (a child with >= 2 parents): route every parent through ONE shared
  //    horizontal bus below them — each parent drops to the bus with its % label,
  //    and a single line drops from the bus into the child. Parallel verticals on
  //    one bus cannot cross; the previous per-parent staggered buses did (a middle
  //    parent's drop passed straight through a leftward parent's higher bus).
  //  - SINGLE-PARENT child: a 3-segment elbow, with each PARENT getting its own
  //    bus height so two distinct parents' fan-OUT buses never merge (a shared bus
  //    would assert ownership that does not exist).
  const OWN_STYLE = { lineColor: OWN_COLOR, lineWeight: OWN_WEIGHT, fontSize: 9, fontColor: pal.ink };
  const childEdges: number[][] = [];
  for (let i = 0; i < n; i++) childEdges[i] = [];
  for (let e = 0; e < edges.length; e++) childEdges[edges[e].c].push(e);

  for (let c = 0; c < n; c++) {
    const ces = childEdges[c];
    if (ces.length === 0) continue;
    const cb = nodes[c].box;
    const childTop = cb.y;
    const ccx = cb.x + cb.w / 2;

    if (ces.length >= 2) {
      // FAN-IN — one shared bus bar.
      let maxParentBottom = -Infinity;
      let minX = ccx;
      let maxX = ccx;
      for (const e of ces) {
        const pbx = nodes[edges[e].p].box;
        const px = pbx.x + pbx.w / 2;
        maxParentBottom = Math.max(maxParentBottom, pbx.y + pbx.h);
        minX = Math.min(minX, px);
        maxX = Math.max(maxX, px);
      }
      const busY = maxParentBottom + (childTop - maxParentBottom) * 0.5;
      for (const e of ces) {
        const pbx = nodes[edges[e].p].box;
        const px = pbx.x + pbx.w / 2;
        const pct = edges[e].own.pct;
        prims.push({
          kind: "line",
          x1: px,
          y1: pbx.y + pbx.h,
          x2: px,
          y2: busY,
          label: pct != null ? pct + "%" : undefined,
          dashed: edges[e].own.dashed === true,
          style: OWN_STYLE,
        } as LinePrim);
      }
      prims.push(
        { kind: "line", x1: minX, y1: busY, x2: maxX, y2: busY, style: OWN_STYLE } as LinePrim,
        { kind: "line", x1: ccx, y1: busY, x2: ccx, y2: childTop, style: OWN_STYLE } as LinePrim
      );
      continue;
    }

    // SINGLE-PARENT — 3-segment elbow with a per-parent bus height.
    const e = ces[0];
    const parent = nodes[edges[e].p];
    const pb = parent.box;
    const px = pb.x + pb.w / 2;
    const parentBottom = pb.y + pb.h;
    // Drop straight down if the parent sits over the child, else enter at center.
    const inset = Math.min(24, cb.w / 4);
    const overChild = px >= cb.x + inset && px <= cb.x + cb.w - inset;
    const entryX = overChild ? px : ccx;
    const ptier = tiers[parent.tier];
    const share = ptier.length <= 1 ? 0.5 : 0.34 + (0.32 * parent.posInTier) / (ptier.length - 1);
    const midY = parentBottom + (childTop - parentBottom) * clamp(share, 0.25, 0.75);
    const dashed = edges[e].own.dashed === true;
    const pct = edges[e].own.pct;
    prims.push(
      { kind: "line", x1: px, y1: parentBottom, x2: px, y2: midY, dashed: dashed, style: OWN_STYLE } as LinePrim,
      { kind: "line", x1: px, y1: midY, x2: entryX, y2: midY, dashed: dashed, style: OWN_STYLE } as LinePrim,
      {
        kind: "line",
        x1: entryX,
        y1: midY,
        x2: entryX,
        y2: childTop,
        label: pct != null ? pct + "%" : undefined,
        dashed: dashed,
        style: OWN_STYLE,
      } as LinePrim
    );
  }

  // 5. Entity boxes (shape + border by type).
  for (let i = 0; i < n; i++) {
    const nd = nodes[i];
    const b = nd.box;
    prims.push({
      kind: shapeFor(nd.entity.type),
      x: b.x,
      y: b.y,
      w: b.w,
      h: b.h,
      text: nd.entity.name,
      sub: subLine(nd.entity),
      dashed: dashedFor(nd.entity.type) || undefined,
      style: {
        fill: fillFor(nd.entity),
        lineColor: pal.ink,
        lineWeight: BOX_BORDER_WEIGHT,
        fontColor: pal.ink,
        // Stepped down by the text fitter when the name + sub-line would otherwise
        // wrap past three lines and clip inside the shape.
        fontSize: nd.fontPt,
        bold: true,
        align: "center",
      },
    } as BoxPrim);
  }

  // 7. Flow edges (cash/equity/asset movement) — a dashed, colored, arrow-
  // headed layer drawn on top of the boxes.
  const flows = model.flows || [];
  const pairKey = (a: number, b: number) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const pairOrdinal: Record<string, number> = {};
  for (let i = 0; i < flows.length; i++) {
    const fl = flows[i];
    const fi = byId[fl.from];
    const ti = byId[fl.to];
    if (fi == null || ti == null || fi === ti) continue;
    const key = pairKey(fi, ti);
    const laneIdx = (pairOrdinal[key] = (pairOrdinal[key] ?? -1) + 1);
    const off = FLOW_OFFSET + laneIdx * FLOW_LANE_GAP;
    const color = stripHash(fl.color) || FLOW_COLORS[fl.kind || "other"] || FLOW_COLORS.other;
    const fb = nodes[fi].box;
    const tb = nodes[ti].box;
    const fcx = fb.x + fb.w / 2;
    const fcy = fb.y + fb.h / 2;
    const tcx = tb.x + tb.w / 2;
    const tcy = tb.y + tb.h / 2;
    const dx = tcx - fcx;
    const dy = tcy - fcy;

    let sx: number;
    let sy: number;
    let ex: number;
    let ey: number;
    if (Math.abs(dx) >= Math.abs(dy)) {
      // horizontal-dominant: exit/enter left or right edges
      sx = dx >= 0 ? fb.x + fb.w : fb.x;
      ex = dx >= 0 ? tb.x : tb.x + tb.w;
      sy = fcy + off;
      ey = tcy + off;
    } else {
      // vertical-dominant: exit/enter top or bottom edges
      sy = dy >= 0 ? fb.y + fb.h : fb.y;
      ey = dy >= 0 ? tb.y : tb.y + tb.h;
      sx = fcx + off;
      ex = tcx + off;
    }
    // Keep the flow layer inside the region.
    sx = clamp(sx, reg.x, reg.x + reg.w);
    ex = clamp(ex, reg.x, reg.x + reg.w);
    sy = clamp(sy, reg.y, reg.y + reg.h);
    ey = clamp(ey, reg.y, reg.y + reg.h);

    const dashed = fl.dashed !== false; // flows are dashed by convention
    prims.push({
      kind: "line",
      x1: sx,
      y1: sy,
      x2: ex,
      y2: ey,
      dashed: dashed,
      style: { lineColor: color, lineWeight: FLOW_WEIGHT },
    } as LinePrim);

    // Arrowhead at the `to` end. Flow lines are axis-aligned, so the head is one
    // of four cardinal directions — emit that direction and let the renderer pick
    // a directional geometry (no rotation, which is unreliable below API 1.10).
    const arrowDir: "up" | "down" | "left" | "right" =
      Math.abs(dx) >= Math.abs(dy) ? (dx >= 0 ? "right" : "left") : dy >= 0 ? "down" : "up";
    prims.push({
      kind: "triangle",
      x: ex - ARROW / 2,
      y: ey - ARROW / 2,
      w: ARROW,
      h: ARROW,
      arrowDir: arrowDir,
      style: { fill: color, lineColor: color, lineWeight: 1 },
    } as BoxPrim);

    if (fl.label) {
      // Put the label on the drawn (axis-aligned) leg, not the diagonal midpoint —
      // the connector is rendered as an L, whose diagonal midpoint is in the empty
      // elbow. Horizontal-dominant → mid of the horizontal leg (at sy); vertical → mid of the vertical leg (at sx).
      const horizontal = Math.abs(dx) >= Math.abs(dy);
      const lx = (horizontal ? (sx + ex) / 2 : ex) - 30;
      const ly = (horizontal ? sy : (sy + ey) / 2) - 8;
      prims.push({
        kind: "text",
        x: lx,
        y: ly,
        w: 60,
        h: 16,
        text: fl.label,
        style: { fontSize: 9, fontColor: color, align: "center" },
      } as TextPrim);
    }
  }

  if (report) {
    let minFont = 11;
    for (let i = 0; i < n; i++) if (nodes[i].fontPt < minFont) minFont = nodes[i].fontPt;
    // Width a row of `maxCount` boxes needs to sit at NODE_W_MIN with normal gaps.
    const recommendMinW = maxCount * NODE_W_MIN + (maxCount + 1) * GAP;
    const cramped = nodeW <= NODE_W_FLOOR + 1 || minFont <= 9 || reg.w < recommendMinW;
    report.entities = n;
    report.tiers = T;
    report.widest_tier = maxCount;
    report.node_w = Math.round(nodeW);
    report.min_font_pt = minFont;
    report.cramped = cramped;
    report.recommend_min_w = Math.round(recommendMinW);
    if (cramped) {
      report.advice =
        `This structure is dense for the region (${n} entities, up to ${maxCount} across a row; ` +
        `boxes rendered at ${Math.round(nodeW)}pt wide, text down to ${minFont}pt). It needs about ` +
        `${Math.round(recommendMinW)}pt of width to read comfortably. Consider giving the diagram its ` +
        `own full-width slide, or splitting it (e.g. an upstream/holding view and a downstream/operating ` +
        `view, or pre-close vs post-close) so each slide stays legible.`;
    }
  }

  return prims;
}

/* --- helpers --------------------------------------------------------------- */

function clamp(v: number, lo: number, hi: number): number {
  if (hi < lo) return lo; // degenerate region: fail safe to the low bound
  return Math.max(lo, Math.min(hi, v));
}

// Re-index nodes to match their current order within a tier array.
function syncPos(arr: number[], nodes: Node[]): void {
  for (let k = 0; k < arr.length; k++) nodes[arr[k]].posInTier = k;
}

// Stable sort of `arr` by `keys` (parallel array), tie-broken by input index
// so the result is deterministic. Sorts `arr` in place.
function stableSortByKey(arr: number[], keys: number[]): void {
  const idx: number[] = [];
  for (let i = 0; i < arr.length; i++) idx[i] = i;
  idx.sort((a, b) => {
    if (keys[a] !== keys[b]) return keys[a] - keys[b];
    return a - b; // stable: preserve prior order on ties
  });
  const sorted: number[] = [];
  for (let i = 0; i < idx.length; i++) sorted[i] = arr[idx[i]];
  for (let i = 0; i < arr.length; i++) arr[i] = sorted[i];
}
